import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaEye, FaPlus } from "react-icons/fa";
import { useTable, useSortBy, useGlobalFilter } from "react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import "./DriverChecklistList.css";

const DriverChecklistList = () => {
  const [checklists, setChecklists] = useState([]);
  const [loggedInDriver] = useState("");
  const [userRole, setUserRole] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); 
  const navigate = useNavigate();

  // Add debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setGlobalFilter(searchInput); 
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const fetchChecklists = async () => {
      try {
        const token = localStorage.getItem("token");
        const role = localStorage.getItem("userrole");

        let url = `${process.env.REACT_APP_BASE_API_URL}/driver-checklists`;
        if (statusFilter !== "all") {
          url += `?status=${statusFilter}`;
        }

        if (role === "driver") {
          const driverName = localStorage.getItem("driverName");
          const driverId = localStorage.getItem("driverId");
          if (driverName && driverId) {
            url = `${
              process.env.REACT_APP_BASE_API_URL
            }/driver-checklists/${encodeURIComponent(
              driverName
            )}/${encodeURIComponent(driverId)}${
              statusFilter !== "all" ? `?status=${statusFilter}` : ""
            }`;
          }
        }

        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // handle wrapped response: { success: true, data: [...] }
        let data = response.data;
        if (data && data.data) data = data.data;
        if (data && !Array.isArray(data)) data = [];

        // normalize all keys to lowercase
        const normalized = (Array.isArray(data) ? data : []).map((item) => {
          const norm = {};
          Object.keys(item || {}).forEach((k) => {
            norm[k.toLowerCase()] = item[k];
          });
          return norm;
        });

        setChecklists(normalized);
      } catch (error) {
        console.error("Error fetching checklists:", error);
        setChecklists([]);
      }
    };

    fetchChecklists();
  }, [statusFilter]); // Add statusFilter to dependency array

  const filteredChecklists = useMemo(() => {
    return checklists.filter((checklist) =>
      globalFilter
        ? Object.values(checklist)
            .join(" ")
            .toLowerCase()
            .includes(globalFilter.toLowerCase())
        : true
    );
  }, [checklists, globalFilter]);

  const columns = useMemo(
    () => [
      { Header: "Checklist No", accessor: "sr_no" },
      { Header: "Driver Name", accessor: "drivername" },
      { Header: "Badge Number", accessor: "badgenumber" },
      { Header: "Fleet Number", accessor: "fleetnumber" },
      { Header: "KM Reading", accessor: "kmreading" },
      { Header: "Location", accessor: "location" },
      {
        Header: "Date",
        accessor: "checklistdate",
        Cell: ({ value }) => {
          const date = new Date(value);
          return date.toLocaleDateString("en-GB");
        },
      },
      {
        Header: "Status",
        accessor: "checkliststatus",
        Cell: ({ value }) => (
          <span className={`status-badge ${value}`}>
            {value ? value.toUpperCase() : "N/A"}
          </span>
        ),
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div>
            <FaEye
              className="action-icon view-icon"
              title="View"
              onClick={() => handleView(row.original.sr_no)}
            />
            <FaEdit
              className="action-icon edit-icon"
              title="Edit"
              onClick={() => handleEdit(row.original.sr_no)}
            />
          </div>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: filteredChecklists,
      },
      useGlobalFilter,
      useSortBy
    );

  const tableContainerRef = useRef();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 50,
  });

  const handleView = (id) => {
    navigate(`/driver-checklists/view/${encodeURIComponent(id)}`);
  };

  const handleEdit = (id) => {
    navigate(`/driver-checklists/edit/${encodeURIComponent(id)}`);
  };

  const handleCreateNew = () => {
    navigate("/driver-checklists/new");
  };

  useEffect(() => {
    const role = localStorage.getItem("userrole");
    setUserRole(role);
  }, []);

  return (
    <div>
      <div className="main-page-button-container">
        <button
          className="driver-navigate-button"
          onClick={() => navigate("/main")}
        >
          Main Page
        </button>
        <div className="filter-container">
          <label>Filter By Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter-dropdown"
          >
            <option value="all">All Status</option>
            <option value="pass">Pass</option>
            <option value="fail">Fail</option>
          </select>
        </div>
      </div>

      <div className="driver-checklist-list-container">
        {/* Header Section */}
        <div className="driver-checklist-list-header">
          <div className="left-button">
            {userRole === "driver" && (
              <button
                onClick={handleCreateNew}
                className="create-new-checklist-button"
              >
                <FaPlus className="add-icon" /> Create New Checklist
              </button>
            )}
          </div>
          <div className="right-button">
            <input
              type="text"
              placeholder="Search..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="search-inputchecklist"
            />
          </div>
        </div>

        <div
          className="driverchecklisttable-container"
          ref={tableContainerRef}
          style={{ height: "600px", overflow: "auto" }}
        >
          <table {...getTableProps()} className="driver-checklist-list-table">
            <thead>
              {headerGroups.map((headerGroup) => {
                const { key: headerGroupKey, ...headerGroupProps } =
                  headerGroup.getHeaderGroupProps();
                return (
                  <tr key={headerGroupKey} {...headerGroupProps}>
                    {headerGroup.headers.map((column) => {
                      const { key: columnKey, ...columnProps } =
                        column.getHeaderProps(column.getSortByToggleProps());
                      return (
                        <th key={columnKey} {...columnProps}>
                          {column.render("Header")}
                          <span>
                            {column.isSorted
                              ? column.isSortedDesc
                                ? " 🔽"
                                : " 🔼"
                              : ""}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index];
                prepareRow(row);
                const { key: rowKey, ...rowProps } = row.getRowProps();
                return (
                  <tr key={rowKey} {...rowProps} style={{ height: "15px" }}>
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } =
                        cell.getCellProps();
                      return (
                        <td key={cellKey} {...cellProps}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DriverChecklistList;
